import { View, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import MarkdownRenderer from "../custom-markdown";
import { CollaborativeGroupWorkResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface CollaborativeCardProps {
  collaborative: CollaborativeGroupWorkResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  section: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  rolesSection: {
    backgroundColor: "#dbeafe",
  },
  stepsSection: {
    backgroundColor: "#dcfce7",
  },
  reflectionSection: {
    backgroundColor: "#f3e8ff",
  },
  evaluationSection: {
    backgroundColor: "#fef3c7",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 8,
  },
  rolesTitle: {
    color: "#2563eb",
  },
  stepsTitle: {
    color: "#16a34a",
  },
  reflectionTitle: {
    color: "#9333ea",
  },
  evaluationTitle: {
    color: "#d97706",
  },
  contentText: {
    fontSize: 9,
    color: "#374151",
    lineHeight: 1.4,
  },
  itemContainer: {
    marginLeft: 8,
    marginBottom: 8,
    borderLeftWidth: 1,
    borderLeftColor: "#e5e7eb",
    paddingLeft: 8,
  },
});

const CollaborativeDocument = ({
  collaborative,
  customizationSettings,
}: CollaborativeCardProps) => {
  const { t } = useTranslation();

  return (
    <PdfLayout
      courseName={collaborative.courseName || ""}
      gradeLevel={collaborative.gradeLevel || 0}
      topic={collaborative.title || ""}
      imageUrl={collaborative.imageUrl}
      author={collaborative.authorName || ""}
      createdDate={collaborative.createdAt || ""}
      headerTextColor="#2563eb"
      customizationSettings={customizationSettings}
    >
      {/* Group Roles Section */}
      <View style={[styles.section, styles.rolesSection]}>
        <MarkdownRenderer
          content={t("collaborative.groupRoles")}
          customStyle={{
            text: {
              text: {
                ...styles.sectionTitle,
                ...styles.rolesTitle,
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: {
                marginBottom: 8,
              },
            },
          }}
        />
        {collaborative.groupRoles?.map((role, index) => (
          <View key={index} style={styles.itemContainer}>
            <MarkdownRenderer
              content={role.title}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                    fontWeight: "bold",
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={
                t("collaborative.responsibilities") +
                ": " +
                role.responsibilities
              }
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={
                t("collaborative.skillsDeveloped") +
                ": " +
                role.skills_developed
              }
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
          </View>
        ))}
      </View>

      {/* Process Steps Section */}
      <View style={[styles.section, styles.stepsSection]} wrap={false}>
        <MarkdownRenderer
          content={t("collaborative.processSteps")}
          customStyle={{
            text: {
              text: {
                ...styles.sectionTitle,
                ...styles.stepsTitle,
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: {
                marginBottom: 8,
              },
            },
          }}
        />
        {collaborative.processSteps?.map((step, index) => (
          <View key={index} style={styles.itemContainer}>
            <MarkdownRenderer
              content={`${t("collaborative.step")} ${step.step_number}: ${step.title}`}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                    fontWeight: "bold",
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={step.description}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={
                t("collaborative.timeEstimate") + ": " + step.time_estimate
              }
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={
                t("collaborative.deliverables") + ": " + step.deliverables
              }
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
          </View>
        ))}
      </View>

      {/* Reflection Questions Section */}
      <View style={[styles.section, styles.reflectionSection]} wrap={false}>
        <MarkdownRenderer
          content={t("collaborative.reflectionQuestions")}
          customStyle={{
            text: {
              text: {
                ...styles.sectionTitle,
                ...styles.reflectionTitle,
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: {
                marginBottom: 8,
              },
            },
          }}
        />
        {collaborative.reflectionQuestions?.map((item, index) => (
          <View key={index} style={styles.itemContainer}>
            <MarkdownRenderer
              content={t("collaborative.question") + ": " + item.question}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={t("collaborative.purpose") + ": " + item.purpose}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
          </View>
        ))}
      </View>

      {/* Peer Evaluation Section */}
      <View style={[styles.section, styles.evaluationSection]} wrap={false}>
        <MarkdownRenderer
          content={t("collaborative.peerEvaluation")}
          customStyle={{
            text: {
              text: {
                ...styles.sectionTitle,
                ...styles.evaluationTitle,
                fontSize: scaleFontSize(
                  styles.sectionTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
              container: {
                marginBottom: 8,
              },
            },
          }}
        />
        {collaborative.peerEvaluationQuestions?.map((item, index) => (
          <View key={index} style={styles.itemContainer}>
            <MarkdownRenderer
              content={t("collaborative.question") + ": " + item.question}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={t("collaborative.criteria") + ": " + item.criteria}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
            <MarkdownRenderer
              content={t("collaborative.scale") + ": " + item.scale}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
          </View>
        ))}
      </View>
    </PdfLayout>
  );
};

function CollaborativeCardPDF(props: CollaborativeCardProps) {
  return <CollaborativeDocument {...props} />;
}

export default CollaborativeCardPDF;
